import print from 'print-js';

import { initGrid } from 'utils/grid/grid';

import { downloadManageInventoryReportByTab } from 'manage-inventory/actions/download-report-by-tab.actions';
import { onItemListSelection } from 'manage-inventory/actions/manage-inventory.actions';
import { fetchPacksList } from '../../actions/fetch-packs-list.actions';
import { fetchPacksGrossValue } from '../../actions/fetch-pack-list-gross-value.actions';

import template from './inventory-packs-tab.html';

const mapStateToThis = ({ dictionary, manageInventory, ui, config, session }) => {
  const { pricePoints, packStatuses } = dictionary;
  const { packs, isLoading, packGrossValue } = manageInventory;
  const { pageSizeKey, sortColumnKey, defaultPageSizes, usesPortionedPacks } = config;
  const { pagination, activeTab } = ui;
  const { locale } = session;
  const terms = dictionary.terms[locale];

  return {
    packs,
    terms,
    isLoading,
    activeTab,
    pagination,
    pricePoints,
    pageSizeKey,
    packStatuses,
    sortColumnKey,
    packGrossValue,
    defaultPageSizes,
    usesPortionedPacks,
  };
};

const mapDispatchToThis = {
  fetchPacksList,
  fetchPacksGrossValue,
  onItemListSelection,
  downloadManageInventoryReportByTab,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$stateParams', 'gettextCatalog', 'Notifications', 'tableService')
class InventoryPacksTabController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
    this.print = print;
  }

  $onInit() {
    this.tableConfig = this.tableService.initConfig({ configName: 'inventoryPacksTab' });
    this.actions = this.getActions();
    this.headers = this.tableConfig.getHeaders(this.getHeaders());
    /// {{ pack }} is Lottery term using to determine Pack.
    this.noDataText = this.gettextCatalog.getString('{{ pack }} not found.', { pack: this.terms.PACK.singular });
    this.defaultParams = {
      [this.pageSizeKey]: this.defaultPageSizes[2],
      retailerId: this.$stateParams.retailerNo,
    };
    this.gridModel = initGrid(this.fetchPacksList, this.defaultParams);
    this.preserveFilters();
    this.search();
    this.packValueTitle = this.usesPortionedPacks
      ? this.gettextCatalog.getString('Total {{ packs }} / {{ portion }} Value', {
          packs: this.terms.PACK.plural,
          portion: this.terms.PORTION.singular,
        })
      : this.gettextCatalog.getString('Total {{ packs }} Value', {
          packs: this.terms.PACK.plural,
        });
  }

  $onDestroy() {
    this.unsubscribe();
  }

  getActions() {
    return [
      {
        type: 'download',
        function: this.onDownloadReport.bind(this),
        extension: 'xlsx',
      },
      {
        type: 'print',
        function: this.handlePrint.bind(this),
      },
    ];
  }

  getHeaders() {
    return [
      {
        text: this.gettextCatalog.getString('Game No.'),
        field: 'gameNumber',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Game Name'),
        field: 'gameName',
        sortable: true,
      },
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.gettextCatalog.getString('{{ pack }} No.', { pack: this.terms.PACK.singular }),
        field: 'packNo',
        sortable: true,
        textAlignment: 'text-right',
      },
      this.usesPortionedPacks
        ? {
            /// {{ pack }} is Lottery term using to determine Pack.
            text: this.gettextCatalog.getString('{{ portion }} No.', { portion: this.terms.PORTION.singular }),
            field: 'portionNumber',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      this.usesPortionedPacks
        ? {
            text: this.gettextCatalog.getString('Ticket Range'),
            field: 'ticketRange',
            sortable: true,
            textAlignment: 'text-right',
          }
        : null,
      {
        /// {{ pack }} is Lottery term using to determine Pack.
        text: this.usesPortionedPacks
          ? this.gettextCatalog.getString('{{ pack }}/{{ portion }} Value', {
              pack: this.terms.PACK.singular,
              portion: this.terms.PORTION.singular,
            })
          : this.gettextCatalog.getString('{{ pack }} Value', { pack: this.terms.PACK.singular }),
        field: 'packValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Price Point'),
        field: 'pieceValue',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Est. % Sold'),
        field: 'soldOutPercent',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Status'),
        field: 'status',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        text: this.gettextCatalog.getString('Days in Status'),
        field: 'daysInStatus',
        sortable: true,
        textAlignment: 'text-right',
      },
      {
        /// {{ issuedMethod }} is Lottery term using to determine issued method.
        text: this.gettextCatalog.getString('Order {{ issuedMethod }}', { issuedMethod: this.terms.ISSUED_METHOD.singular }),
        field: 'displayIssuedMethod',
        sortable: false,
        textAlignment: 'text-right',
      },
    ];
  }

  search() {
    if (this.gamesFilters && this.packFilters.$invalid) return;

    this.gridModel.applyFilters().then(() => {
      this.preserveFilters();
      this.fetchPacksGrossValue(this.preservedFilters);
    });
  }

  clear() {
    this.gridModel.pagingModel.filters = { gameNumber: '' };
    this.search();
  }

  preserveFilters() {
    this.preservedFilters = { ...this.gridModel.pagingModel.filters, retailerId: this.$stateParams.retailerNo };
  }

  searchByPack(packId) {
    const pieces = packId.split('_');
    const pack = {
      gameNumber: pieces[0],
      packNo: pieces[1],
      portionNumber: pieces[2] || 1,
    };
    this.packDetails(pack);
  }

  packDetails(pack) {
    this.onItemListSelection(pack);
    const hideOrderDetails = pack.orderId === null;

    this.Notifications.showComplexDialog({
      component: 'detailsDialog',
      resolve: {
        hideOrderDetails,
      },
    });
  }

  onDownloadReport() {
    const pagingModel = {
      ...this.gridModel.pagingModel,
      filters: this.preservedFilters,
      pageSize: 0, // BE contract - pageSize = 0 triggers exporting entire data set
    };

    const requestParams = {
      ...pagingModel,
      tabId: this.activeTab,
      retailerId: this.$stateParams.retailerNo,
    };

    return this.downloadManageInventoryReportByTab(requestParams);
  }

  handlePrint() {
    this.print({
      printable: 'inventory-packs-tab',
      type: 'html',
    });
  }
}

export default class InventoryPacksTabComponent {
  constructor() {
    this.controller = InventoryPacksTabController;
    this.template = template;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/manage-inventory/components/inventory-packs-tab/inventory-packs-tab-component.js